<template>
  <div class="h-100 my-5">
    <b-button
      type="submit"
      class="myButton"
      @click="$router.go(-1)">
      {{ $t('common.Cancel') }}
    </b-button>
    <div class="dp02 p-3 mt-3">
      <div class="text-center col-lg-12">
        <h2>{{ $t('EditUser.Header') }} {{ $store.state.createEditUser.email }}</h2>
      </div>
      <transition name="slide-fade-main"
                  mode="out-in"
                  appear>
        <ValidationObserver
          ref="observer"
          v-slot="{invalid}"
          tag="form"
          class="col-lg-12 mt-5"
          @submit.prevent="submit()">
          <div class="col-lg-12 row">
            <div class="col-lg-1 offset-lg-4">
              <label for="role">{{ $t('entities.Roles.Singular') }}</label>
            </div>
            <div class="col-lg-3">
              <v-multiselect
                ref="multiselect"
                v-model="role"
                label="name"
                track-by="id"
                :options="availableRoles" />
            </div>
          </div>
          <div class="col-lg-12 row mt-3">
            <div class="col-lg-1 offset-lg-4">
              <label for="color">{{ $t('common.Color') }}</label>
            </div>
            <div class="col-lg-3">
              <ColorInput v-model="color" />
            </div>
          </div>
          <div class="col-lg-12 row mt-3"
               v-if="role != null && (role.name === 'Lékař' || role.name === 'Doctor')">
            <div class="col-lg-1 offset-lg-4">
              <label for="color">{{ $t('common.IsMainDoctor') }}</label>
            </div>
            <div class="col-lg-3">
              <b-form-checkbox v-model="mainDoctor" />
            </div>
          </div>
          <div class="col-lg-12 row mt-3">
            <div class="col-lg-1 offset-lg-4">
              <label for="color">{{ $t('EditUser.MarkForPasswordReset') }}</label>
            </div>
            <div class="col-lg-3">
              <b-button
                class="myButton align-middle"
                @click="passwordResetModal()">
                Reset
              </b-button>
            </div>
          </div>
          <div class="col-lg-12 text-center">
            <b-button
              class="mt-3 myButton align-middle"
              type="submit">
              {{ $t('EditUser.Header') }}
            </b-button>
            <b-spinner
              v-show="editing"
              variant="primary"
              class="align-middle ml-3"
              label="Spinning" />
          </div>
        </ValidationObserver>
      </transition>
    </div>
    <b-modal id="modal-center"
             ref="confirmedModal"
             centered
             size="lg"
             :static="true">
      <template #modal-header>
        <div class="d-flex justify-content-center w-100 text-center">
          <h3>
            {{ $t('EditUser.MarkPasswordSuccess.Title') }}
          </h3>
        </div>
      </template>
      <p>
        {{ $t('EditUser.MarkPasswordSuccess.Body') }} <strong>{{ newPassword }}</strong>
      </p>
      <p>
        {{ $t('EditUser.MarkPasswordSuccess.Body2') }}
      </p>
      <template #modal-footer="{ cancel, ok }">
        <b-button
          variant="success"
          @click="$refs.confirmedModal.hide(); newPassword = '';">
          OK
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapMutations, mapActions, mapState } from 'vuex';
import ColorInput from '@components/input/ColorInput';

export default {
  name: 'EditUser',
  components: {
    ColorInput
  },
  data() {
    return {
      editing: false,
      newPassword: ''
    };
  },
  computed: {
    id() {
      return this.$route.params.id;
    },
    color: {
      get() {
        return this.$store.state.createEditUser.color;
      },
      set(val) {
        this.setColor(val);
      }
    },
    role: {
      get() {
        return this.$store.state.createEditUser.role;
      },
      set(val) {
        this.setRole(val);
      }
    },
    mainDoctor: {
      get() {
        return this.$store.state.createEditUser.mainDoctor;
      },
      set(val) {
        this.setMainDoctor(val);
      }
    },
    ...mapState('createEditUser', ['availableRoles'])
  },
  watch: {
    // eslint-disable-next-line func-names
    '$i18n.locale': function () {
      this.fetchRoles();
      this.fetchUser();
    }
  },
  mounted() {
    this.resetContext();
    this.setUserId(this.id);
    this.fetchUser();
    this.fetchRoles();
  },
  methods: {
    ...mapMutations({
      setUserId: 'createEditUser/setUserId',
      setColor: 'createEditUser/setColor',
      setRole: 'createEditUser/setRole',
      setMainDoctor: 'createEditUser/setMainDoctor'
    }),
    ...mapActions({
      resetContext: 'createEditUser/resetContext',
      editUser: 'createEditUser/editUser',
      fetchUser: 'createEditUser/fetchUser',
      markPasswordReset: 'createEditUser/markPasswordReset',
      fetchRoles: 'createEditUser/fetchRoles'
    }),
    async submit() {
      const isValid = await this.$refs.observer.validate();
      if (isValid) {
        this.editing = true;
        this.editUser().then(() => {
          this.editing = false;
          this.$root.$bvToast.toast(this.$t('EditUser.SuccessToast.Body'), {
            title: this.$t('EditUser.SuccessToast.Title'),
            variant: 'success',
            toaster: 'b-toaster-top-right'
          });
          this.$root.$router.replace({ name: 'admin.users.view' });
        }).catch(() => {
          this.editing = false;
          this.$root.$bvToast.toast(this.$t('EditUser.FailureToast.Body'), {
            title: this.$t('EditUser.FailureToast.Title'),
            variant: 'danger',
            toaster: 'b-toaster-top-right'
          });
        });
      }
    },
    passwordResetModal() {
      this.$bvModal.msgBoxConfirm(`${this.$t('EditUser.MarkForPasswordReset')}?`, {
        centered: true
      })
        .then((value) => {
          if (value) {
            this.markPasswordReset()
              .then((res) => {
                this.newPassword = res.data.result;
                this.$refs.confirmedModal.show();
              })
              .catch(() => {
                this.$bvModal.msgBoxConfirm(this.$t('EditUser.MarkPasswordFailure.Body'), {
                  title: this.$t('EditUser.MarkPasswordFailure.Title'),
                  size: 'md',
                  buttonSize: 'md',
                  okVariant: 'danger',
                  okTitle: 'OK',
                  footerClass: 'p-2',
                  hideHeaderClose: false,
                  centered: true
                });
              });
          }
        });
    }
  }
};
</script>

<style lang="scss" scoped>
</style>
